import { useEffect, useRef, useState } from "react";
import type { ModeConfig } from "../state/modeConfig";
import type { usePlayerState } from "../hooks/usePlayerState";
import { useGameSession } from "../session/useGameSession";
import { cardMatches } from "../logic/rules";
import { TopBar } from "../components/TopBar";
import { Instructions } from "../screens/Instructions";
import { Results } from "../screens/Results";
import { CountdownOverlay } from "../components/CountdownOverlay";
import { MODE_COUNTDOWN_MS } from "../state/modeConfig";
import { useTranslation } from "../i18n/useTranslation";

type Rule = "color" | "shape" | "count";
type ShapeName = "circle" | "triangle" | "square" | "star";
const SHAPES: ShapeName[] = ["circle", "triangle", "square", "star"];
const COLORS = [
  "var(--color-red)",
  "var(--color-cyan)",
  "var(--color-green)",
  "var(--color-amber)",
];
const RULES: Rule[] = ["color", "shape", "count"];

interface Card {
  colorIndex: number;
  shapeIndex: number;
  count: number;
}

interface RuleSwitchProps {
  config: ModeConfig;
  player: ReturnType<typeof usePlayerState>;
  onExit: () => void;
}

const TARGETS: Card[] = [
  { colorIndex: 0, shapeIndex: 0, count: 1 },
  { colorIndex: 1, shapeIndex: 1, count: 2 },
  { colorIndex: 2, shapeIndex: 2, count: 3 },
  { colorIndex: 3, shapeIndex: 3, count: 1 },
];

function randCard(): Card {
  return {
    colorIndex: Math.floor(Math.random() * 4),
    shapeIndex: Math.floor(Math.random() * 4),
    count: 1 + Math.floor(Math.random() * 3),
  };
}

function pickRule(exclude?: Rule): Rule {
  const choices = RULES.filter((r) => r !== exclude);
  return choices[Math.floor(Math.random() * choices.length)];
}

export function RuleSwitch({ config, player, onExit }: RuleSwitchProps) {
  const game = useGameSession(config.modeId, player);
  const { session } = game;
  const { t } = useTranslation();

  const [activeRule, setActiveRule] = useState<Rule>(() => pickRule());
  const [card, setCard] = useState<Card | null>(null);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);
  const [feedback, setFeedback] = useState<{ text: string; ok: boolean } | null>(null);
  const switchThresholdRef = useRef(Math.max(5, 10 - session.difficulty));

  const lastRoundRef = useRef(-1);
  useEffect(() => {
    if (session.phase.kind !== "playing") return;
    if (lastRoundRef.current === session.roundNum) return;
    lastRoundRef.current = session.roundNum;
    setCard(randCard());
    setFeedback(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.phase, session.roundNum]);

  function sortTo(idx: number): void {
    if (!card || feedback !== null) return;
    // Correct if the chosen target matches the card on the ACTIVE rule.
    // Counts [1,2,3,1] make count-1 ambiguous (two matching targets), so
    // accept any match — a first-match index comparison wrongly rejected
    // the second valid target. Mirrors logic/rules.py and ruleMatch (Swift).
    const correct = cardMatches(TARGETS[idx], card, activeRule);
    if (correct) {
      const newCorrect = consecutiveCorrect + 1;
      setConsecutiveCorrect(newCorrect);
      setConsecutiveWrong(0);
      setFeedback({ text: t("common.correct"), ok: true });
      if (newCorrect >= switchThresholdRef.current) {
        setActiveRule((r) => pickRule(r));
        setConsecutiveCorrect(0);
      }
      game.submitAnswer(true);
    } else {
      setConsecutiveCorrect(0);
      setConsecutiveWrong((w) => w + 1);
      setFeedback({ text: t("common.wrong"), ok: false });
      game.submitAnswer(false);
    }
    window.setTimeout(() => setFeedback(null), 500);
  }

  if (session.phase.kind === "instructions") {
    const threshold = Math.max(5, 10 - session.difficulty);
    return (
      <Instructions
        config={config}
        difficulty={session.difficulty}
        highScore={session.playerState.modeHighScores[config.modeId] ?? 0}
        instructions={[
          t("ruleSwitch.instr.targets"),
          t("ruleSwitch.instr.sort"),
          t("ruleSwitch.instr.figureOut"),
          t("ruleSwitch.instr.switch", { n: threshold }),
        ]}
        difficultyDescription={t("ruleSwitch.diffDesc", { n: threshold })}
        onStart={game.startPlaying}
        onBack={onExit}
        onDifficultyChange={game.setDifficulty}
      />
    );
  }
  if (session.phase.kind === "results") {
    return (
      <Results
        modeName={t(`mode.${config.modeId}.name`)}
        result={session.sessionResult}
        level={session.playerState.level}
        xpProgressFrac={session.playerState.xpProgressFrac}
        onContinue={onExit}
      />
    );
  }

  const hint =
    consecutiveWrong >= 4
      ? activeRule === "color"
        ? t("ruleSwitch.hint.color")
        : activeRule === "shape"
          ? t("ruleSwitch.hint.shape")
          : t("ruleSwitch.hint.count")
      : null;

  return (
    <main className="min-h-screen flex flex-col">
      <TopBar
        modeName={t(`mode.${config.modeId}.name`)}
        score={session.score}
        streak={session.streak.current}
        roundNumber={Math.min(session.roundNum + 1, session.maxRounds)}
        totalRounds={session.maxRounds}
      />

      {/* Wrapper hosts the countdown overlay so it covers the target cards
          too — they must not stay bright/clickable during the get-ready. */}
      <div className="relative flex-1 flex flex-col">
        <p className="text-center text-fg-3 font-mono text-xs mt-3">
          {t("ruleSwitch.streak", { n: consecutiveCorrect })}
        </p>

        <div className="flex justify-center gap-2 px-4 py-5">
          {TARGETS.map((tgt, i) => (
            <CardView
              key={i}
              card={tgt}
              size="small"
              onClick={() => sortTo(i)}
              label={t("ruleSwitch.target", { index: i + 1 })}
            />
          ))}
        </div>

        <section className="flex-1 flex flex-col items-center justify-center px-6 gap-4">
          {card && <CardView card={card} size="large" />}
          <p className="text-fg-3 text-sm">{t("ruleSwitch.tapHint")}</p>
          {feedback && (
            <p className={`text-xl font-medium ${feedback.ok ? "text-green" : "text-red"}`}>
              {feedback.text}
            </p>
          )}
          {hint && <p className="text-purple text-sm">{hint}</p>}
        </section>

        {session.phase.kind === "countdown" && (
          <CountdownOverlay
            durationMs={MODE_COUNTDOWN_MS[config.modeId] ?? 0}
            onSkip={game.finishCountdown}
          />
        )}
      </div>
    </main>
  );
}

interface CardViewProps {
  card: Card;
  size: "small" | "large";
  onClick?: () => void;
  label?: string;
}

function CardView({ card, size, onClick, label }: CardViewProps) {
  const w = size === "small" ? 70 : 140;
  const h = size === "small" ? 88 : 130;
  const shapeSize = size === "small" ? 16 : 28;
  const gap = size === "small" ? 3 : 6;
  const color = COLORS[card.colorIndex % COLORS.length];
  const shape = SHAPES[card.shapeIndex % SHAPES.length];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="rounded-xl bg-bg-2 border-2 border-[var(--color-line-strong)] flex items-center justify-center transition-transform active:scale-95"
      style={{ width: w, height: h, gap }}
      aria-label={label}
    >
      <div className="flex items-center" style={{ gap }}>
        {Array.from({ length: card.count }).map((_, i) => (
          <Shape key={i} shape={shape} color={color} size={shapeSize} />
        ))}
      </div>
    </button>
  );
}

function Shape({ shape, color, size }: { shape: ShapeName; color: string; size: number }) {
  switch (shape) {
    case "circle":
      return (
        <span
          aria-hidden
          style={{ background: color, width: size, height: size, borderRadius: "50%" }}
        />
      );
    case "square":
      return (
        <span
          aria-hidden
          style={{ background: color, width: size, height: size, borderRadius: 2 }}
        />
      );
    case "triangle":
      return (
        <span
          aria-hidden
          style={{
            width: 0,
            height: 0,
            borderLeft: `${size / 2}px solid transparent`,
            borderRight: `${size / 2}px solid transparent`,
            borderBottom: `${size}px solid ${color}`,
          }}
        />
      );
    case "star":
      return (
        <span
          aria-hidden
          style={{
            background: color,
            width: size,
            height: size,
            clipPath:
              "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
          }}
        />
      );
  }
}
